import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { Logo } from "../components/Logo";
import { portalApi, portalErr } from "./portalApi";
import "../test/testTheme.css";

const CLASSES = ["Class 9", "Class 10", "Class 11", "Class 12", "Dropper"];
const EXAMS = ["School", "NEET", "JEE Main", "JEE Advanced", "Other"];
const STREAMS = ["PCM", "PCB", "Other"];
const SUBJECTS = ["Physics", "Chemistry", "Mathematics", "Biology"];
const BLANK = { name: "", email: "", password: "", mobile: "", current_class: "", board: "", target_exam: "", stream: "", subjects: ["Physics"], target_year: "", goals: "" };

const afterLogin = (navigate, data) => {
  if (data?.profile_complete === false) {
    navigate("/portal/profile", { state: { completeProfile: true } });
  } else {
    navigate("/portal");
  }
};

export const GoogleButton = ({ redirectPath = "/portal/auth", label = "Continue with Google", testid = "portal-google-btn" }) => {
  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + redirectPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" className="tp-btn ghost" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }} onClick={googleLogin} data-testid={testid}>
      <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
      {label}
    </button>
  );
};

export default function PortalAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [form, setForm] = useState(BLANK);
  const [otp, setOtp] = useState({ email: "", code: "", sent: false });
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState(location.state?.error || "");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const update = (key, value) => setForm((c) => ({ ...c, [key]: value }));
  const toggleSubject = (s) => setForm((c) => ({ ...c, subjects: c.subjects.includes(s) ? c.subjects.filter((x) => x !== s) : [...c.subjects, s] }));

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const switchMode = (m) => { setMode(m); setError(""); setNotice(""); };

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await portalApi.post("/portal/auth/login", login);
      afterLogin(navigate, data);
    } catch (err) { setError(portalErr(err)); } finally { setLoading(false); }
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      await portalApi.post("/portal/auth/otp/request", { email: otp.email });
      setOtp((c) => ({ ...c, sent: true }));
      setResendIn(60);
      setNotice("Code sent! Check your email inbox (and spam folder).");
    } catch (err) { setError(portalErr(err)); } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await portalApi.post("/portal/auth/otp/verify", { email: otp.email, code: otp.code });
      afterLogin(navigate, data);
    } catch (err) { setError(portalErr(err)); } finally { setLoading(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await portalApi.post("/portal/auth/register", form);
      navigate("/portal");
    } catch (err) { setError(portalErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="tp-shell">
      <div className="tp-topbar"><div className="tp-topbar-inner">
        <a className="tp-brand" href="/" data-testid="portal-auth-brand"><Logo size={24} /><span>PRANJAL SIR <b>PHYSICS</b></span></a>
        <a className="tp-back" href="/">← Back to main site</a>
      </div></div>
      <div className="tp-wrap" style={{ maxWidth: 640, padding: "50px 22px 70px" }}>
        <div className="tp-eyebrow" style={{ textAlign: "center" }}>STUDENT PORTAL</div>
        <div className="admin-tabs" style={{ justifyContent: "center", marginBottom: 18 }} data-testid="portal-auth-tabs">
          <button className={mode === "login" ? "active" : ""} onClick={() => switchMode("login")} data-testid="portal-tab-login">Password</button>
          <button className={mode === "otp" ? "active" : ""} onClick={() => switchMode("otp")} data-testid="portal-tab-otp">Email OTP</button>
          <button className={mode === "register" ? "active" : ""} onClick={() => switchMode("register")} data-testid="portal-tab-register">Register</button>
        </div>
        <div className="tp-panel">
          {error && <p className="tp-error" role="alert" data-testid="portal-auth-error">{error}</p>}
          {notice && <p className="tp-pill ok" style={{ marginBottom: 12 }} data-testid="portal-auth-notice">{notice}</p>}
          <GoogleButton />
          <p className="tp-note" style={{ textAlign: "center", margin: "12px 0" }}>— or {mode === "register" ? "register" : "log in"} with email —</p>
          {mode === "login" && (
            <form onSubmit={submitLogin} data-testid="portal-login-form">
              <h2 className="tp-h2">Welcome back</h2>
              <p className="tp-muted" style={{ marginBottom: 18 }}>Log in with your email and password to see your quizzes, scores and rewards.</p>
              <label className="tp-label">EMAIL<input type="email" required value={login.email} onChange={(e) => setLogin((c) => ({ ...c, email: e.target.value }))} placeholder="you@example.com" data-testid="portal-login-email" /></label>
              <label className="tp-label">PASSWORD<input type="password" required value={login.password} onChange={(e) => setLogin((c) => ({ ...c, password: e.target.value }))} placeholder="Your password" data-testid="portal-login-password" /></label>
              <button className="tp-btn" style={{ width: "100%" }} disabled={loading} type="submit" data-testid="portal-login-submit">{loading ? "Logging in…" : "LOG IN"} <ArrowRight size={16} /></button>
              <p className="tp-note" style={{ marginTop: 14, textAlign: "center" }}>New here? <button type="button" className="tp-back" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => switchMode("register")} data-testid="portal-goto-register">Create your free account →</button></p>
            </form>
          )}
          {mode === "otp" && (
            <form onSubmit={otp.sent ? verifyOtp : sendOtp} data-testid="portal-otp-form">
              <h2 className="tp-h2">Login with a code</h2>
              <p className="tp-muted" style={{ marginBottom: 18 }}>No password needed — we email you a 6-digit code. New email? Your account is created automatically.</p>
              <label className="tp-label">EMAIL<input type="email" required value={otp.email} disabled={otp.sent} onChange={(e) => setOtp((c) => ({ ...c, email: e.target.value }))} placeholder="you@example.com" data-testid="portal-otp-email" /></label>
              {otp.sent && (
                <label className="tp-label">6-DIGIT CODE<input required value={otp.code} onChange={(e) => setOtp((c) => ({ ...c, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="••••••" inputMode="numeric" autoFocus data-testid="portal-otp-code" /></label>
              )}
              <button className="tp-btn" style={{ width: "100%" }} disabled={loading} type="submit" data-testid={otp.sent ? "portal-otp-verify" : "portal-otp-send"}>
                {loading ? "Please wait…" : otp.sent ? <>VERIFY & LOG IN <ArrowRight size={16} /></> : <>EMAIL ME A CODE <Mail size={16} /></>}
              </button>
              {otp.sent && (
                <p className="tp-note" style={{ marginTop: 14, textAlign: "center" }}>
                  Didn&apos;t get it?{" "}
                  {resendIn > 0 ? <span data-testid="portal-otp-timer">Resend in {resendIn}s</span> : (
                    <button type="button" className="tp-back" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => { setOtp((c) => ({ ...c, sent: false, code: "" })); }} data-testid="portal-otp-resend">Send a new code</button>
                  )}
                </p>
              )}
            </form>
          )}
          {mode === "register" && (
            <form onSubmit={submitRegister} data-testid="portal-register-form">
              <h2 className="tp-h2">Create your free account</h2>
              <p className="tp-muted" style={{ marginBottom: 18 }}>One account for daily quizzes, test series, reports, rewards and bookings.</p>
              <div className="tp-grid-2">
                <label className="tp-label">FULL NAME *<input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your full name" data-testid="portal-reg-name" /></label>
                <label className="tp-label">MOBILE *<input required value={form.mobile} onChange={(e) => update("mobile", e.target.value)} placeholder="10-digit number" inputMode="numeric" data-testid="portal-reg-mobile" /></label>
              </div>
              <div className="tp-grid-2">
                <label className="tp-label">EMAIL *<input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" data-testid="portal-reg-email" /></label>
                <label className="tp-label">PASSWORD (MIN 8 CHARS) *<input type="password" required minLength={8} value={form.password} onChange={(e) => update("password", e.target.value)} placeholder="Choose a password" data-testid="portal-reg-password" /></label>
              </div>
              <div className="tp-grid-2">
                <label className="tp-label">CLASS *<select required value={form.current_class} onChange={(e) => update("current_class", e.target.value)} data-testid="portal-reg-class"><option value="">Select class</option>{CLASSES.map((c) => <option key={c}>{c}</option>)}</select></label>
                <label className="tp-label">TARGET EXAM *<select required value={form.target_exam} onChange={(e) => update("target_exam", e.target.value)} data-testid="portal-reg-exam"><option value="">Select exam</option>{EXAMS.map((x) => <option key={x}>{x}</option>)}</select></label>
              </div>
              <div className="tp-grid-2">
                <label className="tp-label">BOARD (OPTIONAL)<input value={form.board} onChange={(e) => update("board", e.target.value)} placeholder="CBSE / ICSE / State…" data-testid="portal-reg-board" /></label>
                <label className="tp-label">TARGET YEAR (OPTIONAL)<input value={form.target_year} onChange={(e) => update("target_year", e.target.value)} placeholder="e.g. 2027" inputMode="numeric" data-testid="portal-reg-year" /></label>
              </div>
              <label className="tp-label">STREAM (OPTIONAL)<select value={form.stream} onChange={(e) => update("stream", e.target.value)} data-testid="portal-reg-stream"><option value="">Select stream</option>{STREAMS.map((s) => <option key={s}>{s}</option>)}</select></label>
              <div style={{ marginBottom: 14 }}>
                <span className="tp-label" style={{ marginBottom: 8 }}>SUBJECTS YOU STUDY</span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="portal-reg-subjects">
                  {SUBJECTS.map((s) => <button type="button" key={s} className={form.subjects.includes(s) ? "tp-slot selected" : "tp-slot"} onClick={() => toggleSubject(s)} data-testid={`portal-subject-${s.toLowerCase()}`}>{s}</button>)}
                </div>
              </div>
              <label className="tp-label">LEARNING GOALS / WEAK TOPICS (OPTIONAL)<textarea rows={2} value={form.goals} onChange={(e) => update("goals", e.target.value)} placeholder="e.g. Struggle with rotational motion numericals…" data-testid="portal-reg-goals" /></label>
              <button className="tp-btn" style={{ width: "100%" }} disabled={loading} type="submit" data-testid="portal-register-submit">{loading ? "Creating your account…" : "CREATE MY ACCOUNT"} <ArrowRight size={16} /></button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
